import React from "react";
import PokerTableVisual from "./table/PokerTableVisual";
import ActionPanel from "./table/ActionPanel";
import MySeatPanelRail from "./table/MySeatPanelRail";

export default function PokerTableViewVisual({
  // stack/amount display
  stackViewMode,
  setStackViewMode,
  bbAmount,

  isDark,
  me,
  previewSeats,
  setPreviewSeats,

  // header-ish for this card
  betting,
  currentBet,

  // stage / rail refs + metrics
  visualStageRef,
  railRef,
  railTopPx,
  railPadBottomPx,
  extraBottomPx,

  // ✅ NEW: px do omijania dolnego raila w Visualu
  railAvoidPx,

  // game + table props
  seats,
  seatState,
  mySeatNo,
  dealerSeat,
  sbSeat,
  bbSeat,

  actionSeat,
  lastActionBySeat,
  board,
  myHole,
  showdownHoles,
  street,
  handId,

  // ✅ end-of-hand result
  showHandResult = false,
  bySeat = null,
  payouts = null,

  // actions / state
  canAct,
  isMyTurn,
  isDealer,
  waitingForDealer,
  dealerStepLabel,

  foldDisabled,
  passiveDisabled,
  aggressiveDisabled,

  passiveType,
  passiveLabel,
  aggressiveType,
  aggressiveLabel,

  hasBet,
  clampedTo,
  minAllowedTo,
  minRaiseTo,
  maxRaiseTo,
  bb,
  potVal,
  toCall,

  setRaiseTo,
  doAction,
  doShowCards,

  // show cards UX
  canRevealPhase,
  hasPublicCards,
  mySt,
}) {
  // =========================
  // ✅ Admin/dev: wymuszenie liczby miejsc + placeholdery bez logowania wielu kont
  // =========================
  const isLayoutAdmin = (() => {
    try {
      const fromMe =
        !!(me?.isAdmin || me?.role === "admin" || (Array.isArray(me?.roles) && me.roles.includes("admin")));
      const fromLs = typeof window !== "undefined" && window.localStorage?.getItem("poker_layout_admin") === "1";
      const fromQuery =
        typeof window !== "undefined" && new URLSearchParams(window.location.search).get("layout") === "1";
      return fromMe || fromLs || fromQuery;
    } catch {
      return false;
    }
  })();

  const occupiedSeatNos = (seats || [])
    .filter((s) => s && s.userId)
    .map((s) => Number(s.seat))
    .filter((n) => Number.isFinite(n));

  const occupiedCount = occupiedSeatNos.length;
  const maxOccupiedSeatNo = occupiedSeatNos.length ? Math.max(...occupiedSeatNos) : 0;

  const minPreview = Math.max(2, occupiedCount || 0, maxOccupiedSeatNo || 0);

  const safePreviewSeats =
    previewSeats == null ? null : Math.max(minPreview, Math.min(8, Number(previewSeats) || minPreview));

  const seatsForVisual = React.useMemo(() => {
    if (!isLayoutAdmin || safePreviewSeats == null) return seats || [];

    const target = safePreviewSeats;
    const existing = new Set((seats || []).map((s) => Number(s?.seat)).filter((n) => Number.isFinite(n)));

    const out = [...(seats || [])];

    // dofill brakujących numerów 1..target placeholderami
    for (let i = 1; i <= target; i++) {
      if (!existing.has(i)) {
        out.push({
          seat: i,
          userId: null,
          name: `Miejsce ${i}`,
          __placeholder: true,
        });
      }
    }

    // sort po numerze seatu, żeby było deterministycznie
    out.sort((a, b) => Number(a?.seat) - Number(b?.seat));
    return out;
  }, [seats, isLayoutAdmin, safePreviewSeats]);

  const myIsWinner = !!showHandResult && mySeatNo != null && Number(payouts?.[String(mySeatNo)] || 0) > 0;

  return (
    <div
      className="poker-card visual-table-card"
      style={{
        marginTop: 14,
        paddingBottom: 0, // nie wydłużamy outer carda (to robiło pustą przestrzeń)
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 900 }}>Podgląd stołu</div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {isLayoutAdmin ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12, opacity: 0.8 }}>Podgląd miejsc:</span>
              <select
                value={safePreviewSeats == null ? "auto" : String(safePreviewSeats)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "auto") return setPreviewSeats?.(null);
                  const n = Math.max(minPreview, Math.min(8, Number(v) || minPreview));
                  setPreviewSeats?.(n);
                }}
                style={{
                  background: "rgba(0,0,0,0.35)",
                  color: "inherit",
                  border: "1px solid rgba(255,165,0,0.35)",
                  borderRadius: 10,
                  padding: "6px 10px",
                  fontWeight: 800,
                  fontSize: 12,
                  outline: "none",
                }}
                title="Wymuś liczbę miejsc (placeholdery), żeby podejrzeć layout bez logowania wielu kont."
              >
                <option value="auto">Auto</option>
                {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n} disabled={n < minPreview}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div style={{ opacity: 0.75, fontSize: 13 }}>
            pot: <b>{betting?.pot ?? 0}</b> · currentBet: <b>{currentBet}</b> · minRaiseTo:{" "}
            <b>{betting?.minRaiseTo ?? 0}</b>
          </div>
        </div>
      </div>

      <div
        className="visual-stage"
        ref={visualStageRef}
        style={{
          marginTop: 12,
          paddingBottom: railPadBottomPx,
        }}
      >
        <PokerTableVisual
          stackViewMode={stackViewMode}
          bbAmount={bbAmount}
          hideMyHoleInSeat
          hideMySeatInTable
          seats={seatsForVisual}
          seatState={seatState}
          mySeatNo={mySeatNo}
          dealerSeat={dealerSeat}
          sbSeat={sbSeat}
          bbSeat={bbSeat}
          actionSeat={actionSeat}
          lastActionBySeat={lastActionBySeat}
          board={board}
          pot={Number(betting?.pot || 0)}
          street={street}
          handId={handId}
          maxSeats={8}
          myHole={myHole}
          showdownHoles={showdownHoles}
          onShowCards={doShowCards}
          renderActionPanel={null}
          extraBottomPx={extraBottomPx}
          // ✅ kluczowe: dopiero to uruchamia “lift + bottom cap”
          railAvoidPx={railAvoidPx || 0}
          // ✅ end-of-hand result
          showHandResult={showHandResult}
          bySeat={bySeat}
          payouts={payouts}
        />

        <div
          ref={railRef}
          className={`visual-right-rail ${canAct ? "visual-right-rail--open" : ""}`}
          data-theme={isDark ? "dark" : "light"}
          style={railTopPx != null ? { top: railTopPx } : undefined}
        >
          <div className={`rail-grid ${canAct ? "rail-grid--two" : "rail-grid--one"}`}>
            <div className={`rail-card rail-card--seat ${isMyTurn ? "rail-card--active" : ""}`}
              style={
                myIsWinner
                  ? {
                      border: "2px solid rgba(80, 255, 140, 0.85)",
                      boxShadow:
                        "0 0 0 1px rgba(80,255,140,0.25) inset, 0 0 18px rgba(80,255,140,0.22)",
                    }
                  : undefined
              }>
              <MySeatPanelRail
                bbAmount={bbAmount}
                setStackViewMode={setStackViewMode}
                stackViewMode={stackViewMode}
                mySeatNo={mySeatNo}
                mySt={mySt}
                myHole={myHole}
                isDealer={isDealer}
                sbSeat={sbSeat}
                bbSeat={bbSeat}
                isMyTurn={isMyTurn}
                canRevealPhase={canRevealPhase}
                hasPublicCards={hasPublicCards}
                onShowCards={doShowCards}
                lastActionBySeat={lastActionBySeat}
                // ✅ end-of-hand result
                showHandResult={showHandResult}
                bySeat={bySeat}
                payouts={payouts}
              />
            </div>

            {canAct ? (
              <div className="rail-card rail-card--actions">
                <ActionPanel
                  bbAmount={bbAmount}
                  stackViewMode={stackViewMode}
                  mySeatNo={mySeatNo}
                  myInHand={!!mySt?.inHand}
                  isMyTurn={isMyTurn}
                  actionSeat={actionSeat}
                  waitingForDealer={waitingForDealer}
                  dealerStepLabel={dealerStepLabel}
                  isDealer={isDealer}
                  canAct={canAct}
                  foldDisabled={foldDisabled}
                  passiveDisabled={passiveDisabled}
                  aggressiveDisabled={aggressiveDisabled}
                  passiveType={passiveType}
                  passiveLabel={passiveLabel}
                  aggressiveType={aggressiveType}
                  aggressiveLabel={aggressiveLabel}
                  hasBet={hasBet}
                  clampedTo={clampedTo}
                  minAllowedTo={minAllowedTo}
                  minRaiseTo={minRaiseTo}
                  maxRaiseTo={maxRaiseTo}
                  bb={bb}
                  potVal={potVal}
                  toCall={toCall}
                  setRaiseTo={setRaiseTo}
                  doAction={doAction}
                  currentBet={currentBet}
                  street={street}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
